import {useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography, typographyStyle} from '@/components/Typography';
import {AnalysisItem} from '@/map/@types';
import {FootTrafficResult} from '@/map/@types/foot-traffic';
import {useFootTraffic} from '@/map/api';
import {FOOTTRAFFIC_TOP_PERCENTAGE} from '@/map/constants';
import {AreaOverlayType, FootTrafficWeekAgeTimeDayType} from '@/map/enums';
import {getMobileFootTrafficTileColor} from '@/map/utils';
import {FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {FootTrafficTiles} from '../../MarkerCluster/FootTrafficTiles';
import {ErrorBox} from './ErrorBox';
import {NoData} from './NoData';
import {ReportContainer} from './StyledComponents';
import {useGA4WithParams} from '@/hooks/tracking';
import {useAtomValue} from 'jotai';
import {mapAgentTypeAtom} from '@/map/atom';

const getPercent = (percent: number) => {
  return percent >= 0 ? `${withComma(Math.round(percent))}명` : '';
};

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

export const FootTrafficReport = ({item, ...props}: IProps) => {
  if (!item.circle?.radius || item.circle.radius > 1_000) {
    return (
      <PaddingBox top={28}>
        <NoData text="유동 인구 정보를 조회할 수 없어요.<br />분석 반경을 1km이하로 변경해주세요." />
      </PaddingBox>
    );
  }

  return <Report item={item} {...props} />;
};

const DAY_TYPE = [
  {
    id: FootTrafficWeekAgeTimeDayType.W,
    label: '평일',
  },
  {
    id: FootTrafficWeekAgeTimeDayType.H,
    label: '주말',
  },
];

const Report = ({item, map}: IProps) => {
  const [dayType, setDayType] = useState<FootTrafficWeekAgeTimeDayType>(
    FootTrafficWeekAgeTimeDayType.W
  );
  const {data, isPending, error} = useFootTraffic(item, {
    dayType,
  });

  const mapAgent = useAtomValue(mapAgentTypeAtom);

  const tracking = useGA4WithParams();

  const changeDayType = ({
    id,
    label,
  }: {
    id: FootTrafficWeekAgeTimeDayType;
    label: string;
  }) => {
    setDayType(id);
    tracking('footTraffic_circle_mobile', {
      radius: item?.[AreaOverlayType.CIRCLE]?.radius,
      dayType: label,
      agent: mapAgent,
    });
  };

  return (
    <>
      <FootTrafficTiles map={map} items={data?.result} />
      <ReportContainer>
        <DayTypeTabs>
          {DAY_TYPE.map((i) => (
            <DayTypeTab
              key={i.id}
              disabled={dayType === i.id}
              onClick={() => changeDayType(i)}
            >
              {i.label}
            </DayTypeTab>
          ))}
        </DayTypeTabs>
        <Content>
          {!!error ? (
            <ErrorBox />
          ) : isPending ? (
            <LoadingBar color={palette.textSecondary} />
          ) : !!data?.result?.length ? (
            <FootTrafficTopPercent data={data} />
          ) : (
            <NoData text="분석 지역 내 유동 인구 정보가 없습니다." />
          )}
        </Content>
      </ReportContainer>
    </>
  );
};

const FootTrafficTopPercent = ({data}: {data: FootTrafficResult}) => {
  if (!data || !data.dataIndex) {
    return;
  }

  const limitPercent =
    FOOTTRAFFIC_TOP_PERCENTAGE[FOOTTRAFFIC_TOP_PERCENTAGE.length - 1];
  const dataIndex = data.dataIndex;

  const minCount = Math.min(
    FOOTTRAFFIC_TOP_PERCENTAGE.length,
    dataIndex.length
  );

  return (
    <div>
      <DataWrapper>
        <ChartWrapper>
          {FOOTTRAFFIC_TOP_PERCENTAGE.map((i) => {
            const {fillColor} = getMobileFootTrafficTileColor(i);

            return <ChartItem key={i} style={{backgroundColor: fillColor}} />;
          })}
        </ChartWrapper>
        <LegendWrapper>
          {dataIndex.map((range, idx) => {
            if (idx >= minCount) return null;

            const percent = FOOTTRAFFIC_TOP_PERCENTAGE[idx];
            const {fillColor} = getMobileFootTrafficTileColor(percent);

            return (
              <LegendItem key={idx}>
                <ColorCircle
                  style={{
                    backgroundColor: fillColor,
                  }}
                />
                <Typography color={palette.gray900}>
                  상위{' '}
                  {idx === 0
                    ? `${percent}%`
                    : `${FOOTTRAFFIC_TOP_PERCENTAGE[idx - 1] + 1}~${percent}%`}
                </Typography>
                <FlexFullGrow />
                <Typography color={palette.gray600}>
                  {getPercent(range[0])}~{getPercent(range[1])}
                </Typography>
              </LegendItem>
            );
          })}
        </LegendWrapper>
      </DataWrapper>
      <Note>
        * 유동인구 밀도가 높았던 상위 {limitPercent}% 지역을 표시합니다.
      </Note>
    </div>
  );
};

const DayTypeTabs = styled.div`
  display: flex;
  height: 40px;
  padding: 3px;
  border-radius: 4px;
  background-color: ${palette.gray150};
`;

const DayTypeTab = styled.button`
  flex: 1;
  padding: 0 16px;
  text-align: center;
  cursor: pointer;
  border: none;
  background: none;
  border-radius: 4px;
  ${{...typographyStyle.medium14}}
  color: ${palette.gray400};
  &:disabled {
    color: ${palette.gray600};
    background-color: ${palette.white};
  }
`;

const Content = styled.div`
  margin-top: 16px;
`;

const DataWrapper = styled.div`
  display: flex;
  gap: 12px;
  display: flex;
  padding: 16px;
  background-color: ${palette.bgGray100};
`;

const ChartWrapper = styled.div`
  border-radius: 100px;
  overflow: hidden;
`;

const ChartItem = styled.div`
  width: 14px;
  height: 32px;
`;

const LegendWrapper = styled.div`
  flex-grow: 1;
  ${{...typographyStyle.medium14}}
`;

const LegendItem = styled.div`
  display: flex;
  width: 100%;
  height: 32px;
  align-items: center;
  font-weight: 500;
  & + & {
    border-top: solid 1px ${palette.gray150};
  }
`;

const ColorCircle = styled.div`
  margin-right: 6px;
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 100%;
`;

const Note = styled.div`
  margin-top: 8px;
  color: #969b9e;
  font-size: 13px;
`;
